import Papa from 'papaparse';

/**
 * Parse CSV file to GeoJSON format
 * Expected CSV columns: lat, lon/lng, name, description, type, etc.
 */
export function parseCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const geojson = csvToGeoJSON(results.data);
          resolve(geojson);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      }
    });
  });
}

/**
 * Parse GeoJSON file
 */
export function parseGeoJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geojson = JSON.parse(e.target.result);
        resolve(geojson);
      } catch (error) {
        reject(new Error('Invalid GeoJSON format'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Convert CSV data to GeoJSON format
 */
function csvToGeoJSON(data) {
  const features = data
    .filter(row => {
      // Check if row has valid coordinates
      const lat = parseFloat(row.lat || row.latitude);
      const lon = parseFloat(row.lon || row.lng || row.longitude);
      return !isNaN(lat) && !isNaN(lon);
    })
    .map(row => {
      const lat = parseFloat(row.lat || row.latitude);
      const lon = parseFloat(row.lon || row.lng || row.longitude);
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat] // GeoJSON uses [lon, lat]
        },
        properties: {
          name: row.name || row.title || 'Unnamed Location',
          description: row.description || row.desc || '',
          type: row.type || row.category || 'unknown',
          status: row.status || '',
          address: row.address || '',
          ...row // Include all other properties
        }
      };
    });

  return {
    type: 'FeatureCollection',
    features
  };
}

/**
 * Filter features by status (vacant, abandoned, derelict, etc.)
 */
export function filterByStatus(geojson, statusFilter) {
  if (!statusFilter || statusFilter === 'all') {
    return geojson;
  }

  return {
    ...geojson,
    features: geojson.features.filter(feature => {
      const status = (feature.properties.status || '').toLowerCase();
      const type = (feature.properties.type || '').toLowerCase();
      const name = (feature.properties.name || '').toLowerCase();
      const description = (feature.properties.description || '').toLowerCase();
      
      const searchTerm = statusFilter.toLowerCase();
      
      return status.includes(searchTerm) || 
             type.includes(searchTerm) || 
             name.includes(searchTerm) ||
             description.includes(searchTerm);
    })
  };
}

/**
 * Store data in IndexedDB for offline use
 */
export async function saveToIndexedDB(key, data) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('UrbanExplorerDB', 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('datasets')) {
        db.createObjectStore('datasets');
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['datasets'], 'readwrite');
      const store = transaction.objectStore('datasets');
      const putRequest = store.put(data, key);
      
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };
  });
}

/**
 * Load data from IndexedDB
 */
export async function loadFromIndexedDB(key) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('UrbanExplorerDB', 1);
    
    request.onerror = () => reject(request.error);
    
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('datasets')) {
        db.createObjectStore('datasets');
      }
    };
    
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['datasets'], 'readonly');
      const store = transaction.objectStore('datasets');
      const getRequest = store.get(key);
      
      getRequest.onsuccess = () => resolve(getRequest.result);
      getRequest.onerror = () => reject(getRequest.error);
    };
  });
}

/**
 * Fetch Australian abandoned/heritage building data from pre-generated files
 */
export async function fetchAustralianData(region = 'all') {
  try {
    console.log('Loading data for region:', region);
    
    // Try to load from pre-generated file
    const filename = `/data/abandoned-buildings-${region}.geojson`;
    
    try {
      const response = await fetch(filename);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Loaded ${data.features?.length || 0} locations from ${filename}`);
        return data;
      }
    } catch (error) {
      console.warn(`Could not load ${filename}, falling back to mock data`);
    }

    // Fallback to mock data
    console.warn('Using mock data');
    return generateMockAustralianData(region);
  } catch (error) {
    console.error('Error loading data:', error);
    return generateMockAustralianData(region);
  }
}

/**
 * Generate mock Australian data for testing/fallback
 */
function generateMockAustralianData(region) {
  const mockData = [
    // // Sydney, NSW
    // { name: 'Abandoned Railway Workshop', lat: -33.8688, lon: 151.2093, type: 'industrial', status: 'abandoned', closureDate: '2015', abandonmentDate: '2016', address: 'Sydney, NSW', state: 'nsw' },
    // { name: 'Derelict Hospital', lat: -33.8750, lon: 151.2050, type: 'hospital', status: 'derelict', closureDate: '2008', address: 'Redfern, NSW', state: 'nsw' },
    // { name: 'Old Warehouses', lat: -33.8600, lon: 151.1900, type: 'warehouse', status: 'vacant', closureDate: '2018', address: 'Pyrmont, NSW', state: 'nsw' },
    
    // // Melbourne, VIC
    // { name: 'Historic Wool Store', lat: -37.8136, lon: 144.9631, type: 'industrial', status: 'abandoned', closureDate: '2010', abandonmentDate: '2012', address: 'Melbourne, VIC', state: 'vic' },
    // { name: 'Former Psychiatric Hospital', lat: -37.8200, lon: 144.9500, type: 'hospital', status: 'derelict', closureDate: '1995', address: 'Kew, VIC', state: 'vic' },
    // { name: 'Abandoned Power Station', lat: -37.8300, lon: 144.9800, type: 'industrial', status: 'ruin', closureDate: '1982', abandonmentDate: '1985', address: 'Richmond, VIC', state: 'vic' },
    
    // // Brisbane, QLD
    // { name: 'Old Sugar Mill', lat: -27.4698, lon: 153.0251, type: 'industrial', status: 'abandoned', closureDate: '2005', abandonmentDate: '2007', address: 'Brisbane, QLD', state: 'qld' },
    // { name: 'Vacant Shopping Centre', lat: -27.4650, lon: 153.0300, type: 'commercial', status: 'vacant', closureDate: '2019', address: 'South Brisbane, QLD', state: 'qld' },
    
    // // Adelaide, SA
    // { name: 'Historic Brewery', lat: -34.9285, lon: 138.6007, type: 'industrial', status: 'abandoned', closureDate: '2000', abandonmentDate: '2002', address: 'Adelaide, SA', state: 'sa' },
    // { name: 'Derelict Theatre', lat: -34.9250, lon: 138.5950, type: 'entertainment', status: 'derelict', closureDate: '1988', address: 'North Adelaide, SA', state: 'sa' },
    
    // // Perth, WA
    // { name: 'Old Grain Silo', lat: -31.9505, lon: 115.8605, type: 'industrial', status: 'abandoned', closureDate: '2012', abandonmentDate: '2014', address: 'Perth, WA', state: 'wa' },
    // { name: 'Abandoned Railway Station', lat: -31.9600, lon: 115.8700, type: 'transport', status: 'ruin', closureDate: '1979', address: 'Fremantle, WA', state: 'wa' },
    
    // // Hobart, TAS
    // { name: 'Historic Prison', lat: -42.8821, lon: 147.3272, type: 'institutional', status: 'ruin', closureDate: '1877', address: 'Port Arthur, TAS', state: 'tas' },
    // { name: 'Old Textile Factory', lat: -42.8800, lon: 147.3300, type: 'industrial', status: 'derelict', closureDate: '1992', address: 'Hobart, TAS', state: 'tas' },
  ];

  // Filter by region if specified
  let filteredData = mockData;
  if (region !== 'all') {
    filteredData = mockData.filter(item => item.state === region);
  }

  // Convert to GeoJSON
  return {
    type: 'FeatureCollection',
    features: filteredData.map(item => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.lon, item.lat]
      },
      properties: {
        name: item.name,
        type: item.type,
        status: item.status,
        closureDate: item.closureDate,
        abandonmentDate: item.abandonmentDate,
        address: item.address,
        state: item.state,
        lat: item.lat,
        lon: item.lon
      }
    }))
  };
}

/**
 * Filter features by multiple criteria
 */
export function filterFeatures(geojson, filters) {
  if (!geojson || !geojson.features) return geojson;

  const { status, age, region } = filters;

  return {
    ...geojson,
    features: geojson.features.filter(feature => {
      const props = feature.properties;
      
      // Status filter
      if (status && status !== 'all') {
        const featureStatus = (props.status || '').toLowerCase();
        if (!featureStatus.includes(status.toLowerCase())) {
          return false;
        }
      }

      // Region filter
      if (region && region !== 'all') {
        const featureState = (props.state || '').toLowerCase();
        if (featureState !== region.toLowerCase()) {
          return false;
        }
      }

      // Age filter (based on closure date or abandonment date)
      if (age && age !== 'all') {
        const ageInYears = calculateAge(props.closureDate || props.abandonmentDate);
        if (!matchesAgeRange(ageInYears, age)) {
          return false;
        }
      }

      return true;
    })
  };
}

/**
 * Calculate years since a date
 */
function calculateAge(dateString) {
  if (!dateString) return 0;
  
  const year = parseInt(dateString);
  if (isNaN(year)) return 0;
  
  const currentYear = new Date().getFullYear();
  return currentYear - year;
}

/**
 * Check if age matches the selected range
 */
function matchesAgeRange(age, range) {
  switch (range) {
    case '0-5':
      return age >= 0 && age <= 5;
    case '5-10':
      return age > 5 && age <= 10;
    case '10-20':
      return age > 10 && age <= 20;
    case '20-50':
      return age > 20 && age <= 50;
    case '50+':
      return age > 50;
    default:
      return true;
  }
}
